import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CanvasRenderer from "../components/CanvasRenderer";
import api from "../api";
import { useAuth } from "../contexts/AuthContext";
import ResizablePanels from "../components/ResizablePanels";
import GridCanvasRenderer from "../components/GridCanvasRenderer";
import CardPropertiesPanel from "../components/CardPropertiesPanel";
import CanvasPropertiesPanel from "../components/CanvasPropertiesPanel";
import MiniMap from '../components/MiniMap';
import CanvasExportButton from '../components/CanvasExportButton';
import StoryExportPanel from '../components/StoryExportPanel';
import ToursMenu from '../components/ToursMenu';
import { sanitizeFilename } from '../components/CanvasExportButton';
import * as htmlToImage from 'html-to-image';

const Canvas = () => {
  const { canvasId } = useParams();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [canvas, setCanvas] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [decks, setDecks] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [cardTypes, setCardTypes] = useState([]);
  const [selectedCanvasCardIds, setSelectedCanvasCardIds] = useState([]);
  const [selectedTextOption, setSelectedTextOption] = useState({});
  const [gridOffset, setGridOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [centerPanelSize, setCenterPanelSize] = useState({ width: 0, height: 0 });
  const [actualCanvasSize, setActualCanvasSize] = useState({ width: 0, height: 0 });
  const centerPanelRef = useRef(null);
  const [unavailableCardTypes, setUnavailableCardTypes] = useState(new Map());
  const [cardAvailability, setCardAvailability] = useState({});
  const [hasInitializedViewport, setHasInitializedViewport] = useState(false);
  const [symbols, setSymbols] = useState([]);
  const [isCreatingRelation, setIsCreatingRelation] = useState(false);
  const [relationType, setRelationType] = useState('');
  const [relationColor, setRelationColor] = useState('#ff0000');
  const [relationStart, setRelationStart] = useState(null); // {cardId, cellX, cellY}
  const [relationEnd, setRelationEnd] = useState(null); // {cardId, cellX, cellY}
  const [relationPath, setRelationPath] = useState(null);
  const [relations, setRelations] = useState([]); // Array of {id, type, color, start, end, path}
  const [selectedRelation, setSelectedRelation] = useState(null); // relation id
  // Dynamic relation types
  const [relationTypes, setRelationTypes] = useState([
    'is parent of',
    'travels to',
    'lives in',
  ]);
  // Story export state
  const [showStoryExport, setShowStoryExport] = useState(false);
  const [addingRelationType, setAddingRelationType] = useState(false);
  const [newRelationType, setNewRelationType] = useState('');
  // PNG export overlay state
  const [isCreatingImage, setIsCreatingImage] = useState(false);
  // Box selection state
  const [isBoxSelecting, setIsBoxSelecting] = useState(false);
  const [boxSelectionStart, setBoxSelectionStart] = useState(null);
  const [boxSelectionEnd, setBoxSelectionEnd] = useState(null);
  // Add state to track which endpoint is being edited in relation edit mode
  const [relationEditPending, setRelationEditPending] = useState(null); // 'start' | 'end' | null
  // User canvases for canvas reference cards
  const [userCanvases, setUserCanvases] = useState([]);

  // Ref for the left panel (where property panels and relation creation live)
  const leftPanelRef = useRef(null);
  // Ref for the right panel (where property panels and relation editing live)
  const rightPanelRef = useRef(null);
  const exportButtonRef = useRef();
  const exportRef = useRef();
  const reservedCellsRef = useRef([]);

  const cleanupReservedCells = () => {
    const now = Date.now();
    reservedCellsRef.current = reservedCellsRef.current.filter(c => now - c.time < 10000); // keep for 10 seconds
  };

  const fetchCardTypes = async (language = null) => {
    try {
      const userLanguage = language || user?.language || 'en';
      const res = await api.get("/api/cards/types", {
        headers: {
          'Accept-Language': userLanguage
        }
      });
      setCardTypes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load card types:", err);
      setCardTypes([]);
    }
  };

  const fetchDecks = async (language = null) => {
    try {
      const userLanguage = language || user?.language || 'en';
      const res = await api.get("/api/decks", {
        headers: {
          'Accept-Language': userLanguage
        }
      });
      // Show ALL decks
      const allDecks = [
        ...(res.data.purchased || []),
        ...(res.data.available || []),
        ...(res.data.unavailable || [])
      ];
      setDecks(allDecks);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to load decks");
    }
  };

  const fetchUserCanvases = async () => {
    try {
      const res = await api.get("/api/canvas");
      setUserCanvases(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load user canvases:", err);
      setUserCanvases([]);
    }
  };

  const fetchCardAvailability = async (targetCanvasId, allowDuplicates) => {
    try {
      const cId = targetCanvasId || canvasId;
      if (!cId) return;
      const allowDups = allowDuplicates !== undefined ? allowDuplicates : canvas?.allow_duplicate_draws;
      const res = await api.get(`/api/canvas/${cId}/availability?allowDuplicates=${allowDups ? 'true' : 'false'}`);
      setCardAvailability(res.data || {});
    } catch (err) {
      console.error("Failed to fetch card availability:", err);
    }
  };

  useEffect(() => {
    const fetchCanvasData = async () => {
      if (!canvasId) return;

      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/api/canvas/${canvasId}`);
        const canvasData = {
          ...res.data.canvas,
          background_image: res.data.canvas.background_image || user?.canvas_background || 'default',
          showImages: user?.show_images !== undefined ? user.show_images : true,
          shuffle_extensions: res.data.canvas.shuffle_extensions !== undefined ? res.data.canvas.shuffle_extensions : (user?.default_shuffle_extensions !== undefined ? user.default_shuffle_extensions : true),
          allow_duplicate_draws: res.data.canvas.allow_duplicate_draws !== undefined ? res.data.canvas.allow_duplicate_draws : (user?.default_allow_duplicate_draws !== undefined ? user.default_allow_duplicate_draws : false),
          language: user?.language
        };
        setCanvas(canvasData);
        setCards(res.data.cards || []);
        // Set selectedTextOption for each card based on stored selected_option_index
        if (Array.isArray(res.data.cards)) {
          setSelectedTextOption(prev => {
            const newOptions = { ...prev };
            res.data.cards.forEach(card => {
              if (card.selected_option_index != null) {
                newOptions[card.canvas_card_id] = card.selected_option_index;
              }
            });
            return newOptions;
          });
        }
        await fetchAndProcessRelations(res.data.canvas.canvas_id, res.data.cards || [], setRelations, setRelationTypes, getGridMap, findPathBetweenCells, res.data.cards || []);

        // Fetch decks, languages, and card types
        await fetchDecks(res.data.canvas.language || user?.language || 'en');
        await fetchCardTypes(res.data.canvas.language || user?.language || 'en');

        const languagesRes = await api.get("/api/users/languages");
        setLanguages(Array.isArray(languagesRes.data) ? languagesRes.data : []);

        // Fetch user canvases for canvas reference cards
        await fetchUserCanvases();

        // Fetch initial card availability
        await fetchCardAvailability(canvasId, canvasData.allow_duplicate_draws);
      } catch (err) {
        setError(err?.response?.data?.error || err.message || "Failed to load canvas");
        if (err?.response?.status === 404) {
          navigate("/dashboard");
        }
      }
      setLoading(false);
    };
    fetchCanvasData();
  }, [canvasId, user?.id]);

  useEffect(() => {
    if (!centerPanelRef.current) return;
    const observer = new window.ResizeObserver(entries => {
      for (let entry of entries) {
        setCenterPanelSize({ width: entry.contentRect.width, height: entry.contentRect.height });
      }
    });
    observer.observe(centerPanelRef.current);

    // Manually trigger a measurement on mount
    const rect = centerPanelRef.current.getBoundingClientRect();
    setCenterPanelSize({ width: rect.width, height: rect.height });

    return () => observer.disconnect();
  }, [centerPanelRef.current]);

  useEffect(() => {
    if (
      centerPanelSize.width > 0 &&
      centerPanelSize.height > 0 &&
      !hasInitializedViewport
    ) {
      // Nudge gridOffset to force a re-render
      setGridOffset(gridOffset => ({ x: gridOffset.x + 0.0001, y: gridOffset.y }));
      setTimeout(() => {
        setGridOffset({ x: 0, y: 0 });
      }, 0);
      setHasInitializedViewport(true);
    }
  }, [centerPanelSize, hasInitializedViewport]);

  useEffect(() => {
    if (!canvas || !canvas.language) return;
    fetchDecks(canvas.language);
    fetchCardTypes(canvas.language);
  }, [canvas && canvas.language]);

  useEffect(() => {
    // Fetch all symbols for use in rendering
    const fetchSymbols = async () => {
      try {
        const res = await api.get('/api/symbols');
        setSymbols(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        // Not critical, but log error
        console.error('Failed to fetch symbols:', err);
        setSymbols([]);
      }
    };
    fetchSymbols();
  }, []);

  // Get card types for selected decks
  const getCardTypesForSelectedDecks = () => {
    if (!canvas?.selected_decks || !cardTypes.length) return [];

    const selectedDeckIds = canvas.selected_decks.map(id => parseInt(id));
    const selectedDecks = decks.filter(deck => selectedDeckIds.includes(deck.deck_id));

    // Group card types by deck
    const cardTypesByDeck = {};

    selectedDecks.forEach(deck => {
      if (deck.card_types) {
        cardTypesByDeck[deck.deck_id] = deck.card_types.map(cardTypeId =>
          cardTypes.find(ct => Number(ct.type_id) === Number(cardTypeId))
        ).filter(Boolean);
      }
    });
    return cardTypesByDeck;
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!canvas) return;

    setSaving(true);
    try {
      await api.put(`/api/canvas/${canvas.canvas_id}`, {
        name: canvas.name,
        language: canvas.language,
        shuffle_extensions: canvas.shuffle_extensions,
        allow_duplicate_draws: canvas.allow_duplicate_draws,
        background_image: canvas.background_image || 'default',
        decks: (canvas.selected_decks || []).filter(Boolean).map(Number)
      });

      // Sync profile settings
      const profileRes = await api.put("/api/users/profile", {
        show_images: canvas.showImages,
        default_shuffle_extensions: canvas.shuffle_extensions,
        default_allow_duplicate_draws: canvas.allow_duplicate_draws,
        language: canvas.language,
        canvas_background: canvas.background_image
      });
      if (profileRes.data && profileRes.data.user) {
        updateUser(profileRes.data.user);
      }

      setError("");
    } catch (err) {
      console.error('Save error:', err);
      setError(err?.response?.data?.error || err.message || "Failed to update canvas");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!canvas) return;

    if (window.confirm(`Are you sure you want to delete "${canvas.name}"? This action cannot be undone.`)) {
      try {
        await api.delete(`/api/canvas/${canvas.canvas_id}`);
        navigate("/dashboard");
      } catch (err) {
        setError(err?.response?.data?.error || err.message || "Failed to delete canvas");
      }
    }
  };

  // Helper: get card texts in canvas language
  const getCardTexts = (card) => {
    if (!card || !card.texts) return [];
    return card.texts.filter(t => t.language === (canvas?.language || 'en'));
  };

  // Helper: get grid map (cellX, cellY) => card
  const getGridMap = (cardsArray) => {
    const map = {};
    for (const card of cardsArray) {
      const key = `${card.pos_x},${card.pos_y}`;
      map[key] = card;
    }
    return map;
  };

  // Find nearest empty cell to center
  const findNearestEmptyCell = () => {
    const gridMap = getGridMap(cards);
    const range = 20; // Search a larger area

    // Calculate the center of the current viewport in cell coordinates
    const CELL_SIZE = 122; // Match the constant in GridCanvasRenderer
    const centerCellX = Math.round(-gridOffset.x / (CELL_SIZE * zoom));
    const centerCellY = Math.round(-gridOffset.y / (CELL_SIZE * zoom));

    for (let d = 0; d <= range; d++) {
      for (let dx = -d; dx <= d; dx++) {
        for (let dy = -d; dy <= d; dy++) {
          if (Math.abs(dx) !== d && Math.abs(dy) !== d) continue;
          const cellX = centerCellX + dx;
          const cellY = centerCellY + dy;
          const key = `${cellX},${cellY}`;
          const isOccupied = gridMap[key] || reservedCellsRef.current.some(c => c.x === cellX && c.y === cellY);
          if (!isOccupied) {
            return { cellX, cellY };
          }
        }
      }
    }
    return { cellX: centerCellX, cellY: centerCellY };
  };

  const reserveCell = (x, y) => {
    reservedCellsRef.current.push({ x, y, time: Date.now() });
  };

  // Add card to nearest empty cell
  const handleDrawCard = async (deckIdsOrDeckId, typeId) => {
    if (!canvas) return;
    setError("");
    try {
      let url = `/api/canvas/${canvas.canvas_id}/draw/${typeId}`;
      let deckKey;
      const allowDups = canvas.allow_duplicate_draws ? 'true' : 'false';
      if (Array.isArray(deckIdsOrDeckId)) {
        url += `?deckIds=${deckIdsOrDeckId.join(',')}&allowDuplicates=${allowDups}`;
        deckKey = deckIdsOrDeckId.sort().join(',');
      } else {
        url += `?deckId=${deckIdsOrDeckId}&allowDuplicates=${allowDups}`;
        deckKey = deckIdsOrDeckId.toString();
      }
      const res = await api.get(url);
      const card = res.data;
      if (!card || card.card_id == null) {
        setError("No available card of this type in this deck.");
        setUnavailableCardTypes(prev => new Map(prev.set(`${deckKey}:${typeId}`, true)));
        return;
      }

      const texts = card.texts || [];

      cleanupReservedCells();

      if (typeId === 7) {
        // Adventure card: plus-shaped cluster with fixed option assignments per position:
        //   top = option 1, centre = option 2, left = option 3, bottom = option 4, right = option 5
        const { cellX: centerX, cellY: centerY } = findNearestEmptyCell();
        reserveCell(centerX, centerY);
        
        // Each leaf carries its fixed option index alongside its grid offset
        const leafOffsets = [
          { dx: 0, dy: -1, optionIndex: 1 }, // top    → option 1
          { dx: -1, dy: 0, optionIndex: 3 }, // left   → option 3
          { dx: 0, dy: 1, optionIndex: 4 }, // bottom → option 4
          { dx: 1, dy: 0, optionIndex: 5 }, // right  → option 5
        ];

        // Find positions for all leaves first!
        const leafPositions = leafOffsets.map(offset => {
          const leafX = centerX + offset.dx;
          const leafY = centerY + offset.dy;
          const isOccupied = getGridMap(cards)[`${leafX},${leafY}`] || reservedCellsRef.current.some(c => c.x === leafX && c.y === leafY);
          let finalPos = { cellX: leafX, cellY: leafY };
          if (isOccupied) {
            finalPos = findNearestEmptyCell();
          }
          reserveCell(finalPos.cellX, finalPos.cellY);
          return { ...offset, finalPos };
        });

        const centreRes = await api.post(`/api/canvas/${canvas.canvas_id}/cards`, {
          cardId: card.card_id,
          posX: centerX,
          posY: centerY,
          groupId: null,
          selected_option_index: 2, // centre → option 2
          user_text: card.user_text || null
        });
        const centreCanvasId = centreRes.data && centreRes.data.canvasCardId;
        const leafPromises = leafPositions.map((leaf) => {
          return api.post(`/api/canvas/${canvas.canvas_id}/cards`, {
            cardId: card.card_id,
            posX: leaf.finalPos.cellX,
            posY: leaf.finalPos.cellY,
            groupId: centreCanvasId,
            adventure_group_id: centreCanvasId,
            selected_option_index: leaf.optionIndex,
            user_text: null
          });
        });
        // Await leaf card creation and collect their canvas IDs
        const leafResponses = await Promise.all(leafPromises);
        // Create relations from centre to each leaf with grey color and type "Adventure"
        const relationPromises = leafResponses.map(res => {
          const leafCanvasId = res.data && res.data.canvasCardId;
          if (!leafCanvasId) return null;
          return api.post(`/api/canvas/${canvas.canvas_id}/relations`, {
            start_canvas_card_id: centreCanvasId,
            end_canvas_card_id: leafCanvasId,
            relation_type: "Adventure",
            relation_color: "#808080"
          });
        }).filter(p => p !== null);
        await Promise.all(relationPromises);
        // Refresh state
        const updated = await api.get(`/api/canvas/${canvas.canvas_id}`);
        setCards(updated.data.cards || []);
        if (Array.isArray(updated.data.cards)) {
          setSelectedTextOption(prev => {
            const newOpts = { ...prev };
            updated.data.cards.forEach(c => {
              if (c.selected_option_index != null) {
                newOpts[c.canvas_card_id] = c.selected_option_index;
              }
            });
            return newOpts;
          });
        }
        await fetchAndProcessRelations(canvas.canvas_id, updated.data.cards || [], setRelations, setRelationTypes, getGridMap, findPathBetweenCells, updated.data.cards || []);
        if (centreCanvasId) {
          setSelectedCanvasCardIds([centreCanvasId]);
          setSelectedTextOption(opt => ({ ...opt, [centreCanvasId]: 2 }));
        }
      } else {
        // Normal card handling: random option
        const { cellX, cellY } = findNearestEmptyCell();
        reserveCell(cellX, cellY);
        const langTexts = texts.filter(t => t.language === (canvas?.language || 'en'));
        const randomOption = langTexts.length > 0 ? langTexts[Math.floor(Math.random() * langTexts.length)].option_index : 1;
        const selectedOption = selectedTextOption[card.card_id] || randomOption;
        const addRes = await api.post(`/api/canvas/${canvas.canvas_id}/cards`, {
          cardId: card.card_id,
          posX: cellX,
          posY: cellY,
          groupId: null,
          selected_option_index: selectedOption,
          user_text: card.user_text || null
        });
        const canvasCardId = addRes.data && addRes.data.canvasCardId;
        const updated = await api.get(`/api/canvas/${canvas.canvas_id}`);
        setCards(updated.data.cards || []);
        if (Array.isArray(updated.data.cards)) {
          setSelectedTextOption(prev => {
            const newOpts = { ...prev };
            updated.data.cards.forEach(c => {
              if (c.selected_option_index != null) {
                newOpts[c.canvas_card_id] = c.selected_option_index;
              }
            });
            return newOpts;
          });
        }
        await fetchAndProcessRelations(canvas.canvas_id, updated.data.cards || [], setRelations, setRelationTypes, getGridMap, findPathBetweenCells, updated.data.cards || []);
        if (canvasCardId) {
          setSelectedCanvasCardIds([canvasCardId]);
          setSelectedTextOption(opt => ({ ...opt, [canvasCardId]: selectedOption }));
        }
      }
      // Refresh card availability after successful draw
      fetchCardAvailability(canvas.canvas_id, canvas.allow_duplicate_draws);
      setError("");
    } catch (err) {
      console.error('Draw card error:', err);
      const errorMessage = err?.response?.data?.error || err.message || "Failed to draw card";
      setError(errorMessage);
      if (errorMessage.includes("No available cards") || errorMessage.includes("No available card")) {
        let deckKey;
        if (Array.isArray(deckIdsOrDeckId)) {
          deckKey = deckIdsOrDeckId.sort().join(',');
        } else {
          deckKey = deckIdsOrDeckId.toString();
        }
        setUnavailableCardTypes(prev => new Map(prev.set(`${deckKey}:${typeId}`, true)));
      }
    }
  };


  const handleMoveCard = async (canvasCardId, cellX, cellY) => {
    if (!canvas) return;
    try {
      // Look up by canvas_card_id (not card_id) so Adventure sub-cards move independently
      const card = cards.find(c => c.canvas_card_id === canvasCardId);
      if (!card) return;
      await api.put(`/api/canvas/${canvas.canvas_id}/cards/${card.canvas_card_id}`, {
        posX: cellX,
        posY: cellY
      });
      // Refetch cards for canvas
      const updated = await api.get(`/api/canvas/${canvas.canvas_id}`);
      setCards(updated.data.cards || []);
      // Set selectedTextOption for each card based on stored selected_option_index
      if (Array.isArray(updated.data.cards)) {
        setSelectedTextOption(prev => {
          const newOptions = { ...prev };
          updated.data.cards.forEach(card => {
            if (card.selected_option_index != null) {
              newOptions[card.canvas_card_id] = card.selected_option_index;
            }
          });
          return newOptions;
        });
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to move card");
    }
  };

  // Multi-selection handlers
  const handleSelectCard = (card, isCtrlClick = false, isAltClick = false) => {
    if (!card) {
      // Clear selection when clicking empty space
      setSelectedCanvasCardIds([]);
      setSelectedRelation(null);
      return;
    }

    // If editing a relation endpoint
    if (relationEditPending && selectedRelation) {
      const rel = relations.find(r => r.id === selectedRelation);
      if (!rel) return;
      if (relationEditPending === 'start') {
        // PATCH backend to update start_canvas_card_id
        api.patch(`/api/canvas/${canvas.canvas_id}/relations/${rel.id}`, {
          start_canvas_card_id: card.canvas_card_id
        }).then(async () => {
          // Refetch and reprocess all relations to get updated paths
          await fetchAndProcessRelations(canvas.canvas_id, cards, setRelations, setRelationTypes, getGridMap, findPathBetweenCells, cards);
          setRelationEditPending(null);
          setRelationStart(null);
        });
      } else if (relationEditPending === 'end') {
        api.patch(`/api/canvas/${canvas.canvas_id}/relations/${rel.id}`, {
          end_canvas_card_id: card.canvas_card_id
        }).then(async () => {
          // Refetch and reprocess all relations to get updated paths
          await fetchAndProcessRelations(canvas.canvas_id, cards, setRelations, setRelationTypes, getGridMap, findPathBetweenCells, cards);
          setRelationEditPending(null);
          setRelationEnd(null);
        });
      }
      return;
    }

    if (isCtrlClick) {
      // Ctrl+click: add/remove from multi-selection
      setSelectedCanvasCardIds(prev => {
        const isSelected = prev.includes(card.canvas_card_id);
        let newSelection;
        if (isSelected) {
          // Remove from selection
          newSelection = prev.filter(id => id !== card.canvas_card_id);
        } else {
          // Add to selection
          newSelection = [...prev, card.canvas_card_id];
        }
        return newSelection;
      });
      setSelectedRelation(null);
    } else {
      // Normal click: select single card
      setSelectedCanvasCardIds([card.canvas_card_id]);
      setSelectedRelation(null);
      handleSelectCardForRelation(card);
    }
  };

  const handleBoxSelectionStart = (startPos) => {
    setIsBoxSelecting(true);
    setBoxSelectionStart(startPos);
    setBoxSelectionEnd(startPos);
  };

  const handleBoxSelectionUpdate = (endPos) => {
    if (isBoxSelecting) {
      setBoxSelectionEnd(endPos);
    }
  };

  const handleBoxSelectionEnd = (finalEnd) => {
    const end = finalEnd || boxSelectionEnd;
    if (!isBoxSelecting || !boxSelectionStart || !end) {
      setIsBoxSelecting(false);
      setBoxSelectionStart(null);
      setBoxSelectionEnd(null);
      return;
    }

    // Calculate the range of cells that were actually dragged through
    const startX = boxSelectionStart.x;
    const startY = boxSelectionStart.y;
    const endX = end.x;
    const endY = end.y;

    // Determine the direction of the drag
    const stepX = startX <= endX ? 1 : -1;
    const stepY = startY <= endY ? 1 : -1;

    // Generate all cells that were actually dragged through
    const selectedCells = [];
    for (let x = startX; stepX > 0 ? x <= endX : x >= endX; x += stepX) {
      for (let y = startY; stepY > 0 ? y <= endY : y >= endY; y += stepY) {
        selectedCells.push({ x, y });
      }
    }

    // Find cards that are in the selected cells
    const selectedCards = cards.filter(card => {
      const cardX = card.pos_x;
      const cardY = card.pos_y;
      const isInSelection = selectedCells.some(cell => cell.x === cardX && cell.y === cardY);
      return isInSelection;
    });

    // Update selection
    const selectedIds = selectedCards.map(card => card.canvas_card_id);
    setSelectedCanvasCardIds(selectedIds);

    // Clear box selection state (after selection)
    setIsBoxSelecting(false);
    setBoxSelectionStart(null);
    setBoxSelectionEnd(null);
  };

  const handleMoveSelectedCards = async (offsetX, offsetY) => {
    if (selectedCanvasCardIds.length === 0) return;

    const gridMap = getGridMap(cards);
    const selectedCards = cards.filter(card => selectedCanvasCardIds.includes(card.canvas_card_id));

    // Check if all target positions are empty (excluding selected cards)
    const canMove = selectedCards.every(card => {
      const newX = card.pos_x + offsetX;
      const newY = card.pos_y + offsetY;
      const targetCard = gridMap[`${newX},${newY}`];
      return !targetCard || selectedCanvasCardIds.includes(targetCard.canvas_card_id);
    });

    if (!canMove) {
      setError("Cannot move cards: target positions are occupied");
      return;
    }

    // Move all selected cards
    try {
      const movePromises = selectedCards.map(card => {
        const newX = card.pos_x + offsetX;
        const newY = card.pos_y + offsetY;
        return api.put(`/api/canvas/${canvas.canvas_id}/cards/${card.canvas_card_id}`, {
          posX: newX,
          posY: newY
        });
      });

      await Promise.all(movePromises);

      // Refetch cards and relations
      const updated = await api.get(`/api/canvas/${canvas.canvas_id}`);
      setCards(updated.data.cards || []);
      // Set selectedTextOption for each card based on stored selected_option_index
      if (Array.isArray(updated.data.cards)) {
        setSelectedTextOption(prev => {
          const newOptions = { ...prev };
          updated.data.cards.forEach(card => {
            if (card.selected_option_index != null) {
              newOptions[card.canvas_card_id] = card.selected_option_index;
            }
          });
          return newOptions;
        });
      }
      await fetchAndProcessRelations(canvas.canvas_id, updated.data.cards || [], setRelations, setRelationTypes, getGridMap, findPathBetweenCells, updated.data.cards || []);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to move cards");
    }
  };

  // Add handler for updating canvas and auto-saving
  const handleCanvasChange = async (newCanvas) => {
    // Check if profile settings changed
    const profileChanged =
      newCanvas.showImages !== canvas.showImages ||
      newCanvas.shuffle_extensions !== canvas.shuffle_extensions ||
      newCanvas.allow_duplicate_draws !== canvas.allow_duplicate_draws ||
      newCanvas.language !== canvas.language ||
      newCanvas.background_image !== canvas.background_image;

    const allowDupsChanged = newCanvas.allow_duplicate_draws !== canvas.allow_duplicate_draws;
    const selectedDecksChanged = JSON.stringify(newCanvas.selected_decks || []) !== JSON.stringify(canvas.selected_decks || []);

    setCanvas(newCanvas);
    setSaving(true);
    try {
      await api.put(`/api/canvas/${newCanvas.canvas_id}`, {
        name: newCanvas.name,
        language: newCanvas.language,
        shuffle_extensions: newCanvas.shuffle_extensions,
        allow_duplicate_draws: newCanvas.allow_duplicate_draws,
        background_image: newCanvas.background_image || 'default',
        decks: (newCanvas.selected_decks || []).filter(Boolean).map(Number)
      });

      if (profileChanged) {
        const profileRes = await api.put("/api/users/profile", {
          show_images: newCanvas.showImages,
          default_shuffle_extensions: newCanvas.shuffle_extensions,
          default_allow_duplicate_draws: newCanvas.allow_duplicate_draws,
          language: newCanvas.language,
          canvas_background: newCanvas.background_image
        });
        if (profileRes.data && profileRes.data.user) {
          updateUser(profileRes.data.user);
        }
      }
      if (allowDupsChanged || selectedDecksChanged) {
        fetchCardAvailability(newCanvas.canvas_id, newCanvas.allow_duplicate_draws);
      }
      setError("");
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to update canvas");
    }
    setSaving(false);
  };

  const handleDeleteMultipleCards = async (canvasCardIds) => {
    if (!canvasCardIds || canvasCardIds.length === 0) return;
    try {
      // Delete cards sequentially to avoid PostgreSQL deadlocks (e.g., when deleting a centre card and its leaves simultaneously)
      for (const canvasCardId of canvasCardIds) {
        const card = cards.find(c => c.canvas_card_id === canvasCardId);
        if (card) {
          await api.delete(`/api/canvas/${canvas.canvas_id}/cards/${card.canvas_card_id}`);
        }
      }
      // Clear selection immediately after delete
      setSelectedCanvasCardIds([]);
      // Refetch cards
      const updated = await api.get(`/api/canvas/${canvas.canvas_id}`);
      setCards(updated.data.cards || []);
      // Refetch relations
      await fetchAndProcessRelations(canvas.canvas_id, updated.data.cards || [], setRelations, setRelationTypes, getGridMap, findPathBetweenCells, updated.data.cards || []);
      // Refresh card availability after card deletion
      fetchCardAvailability(canvas.canvas_id, canvas.allow_duplicate_draws);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to delete cards');
    }
  };

  // Pathfinding utility: A* algorithm to find a path between two cells, preferring empty cells and avoiding lines
  function findPathBetweenCells(start, end, gridMap, lineMap = {}) {
    const directions = [
      [0, 1], [1, 0], [0, -1], [-1, 0], // cardinal
      [1, 1], [1, -1], [-1, 1], [-1, -1] // diagonals
    ];
    const key = (x, y) => `${x},${y}`;
    const openSet = new Set([key(start.cellX, start.cellY)]);
    const cameFrom = {};
    const gScore = { [key(start.cellX, start.cellY)]: 0 };
    const fScore = { [key(start.cellX, start.cellY)]: improvedHeuristic(start, end) };

    // Classic diagonal distance heuristic (no deviation penalty)
    function improvedHeuristic(current, target) {
      return Math.max(Math.abs(current.cellX - target.cellX), Math.abs(current.cellY - target.cellY));
    }

    // Check if a direct line of sight exists (no obstacles in between)
    function hasLineOfSight(from, to) {
      const dx = to.cellX - from.cellX;
      const dy = to.cellY - from.cellY;
      const steps = Math.max(Math.abs(dx), Math.abs(dy));

      if (steps === 0) return true;

      for (let i = 1; i < steps; i++) {
        const x = from.cellX + Math.round((dx * i) / steps);
        const y = from.cellY + Math.round((dy * i) / steps);
        const cellKey = key(x, y);
        if (gridMap[cellKey]) {
          return false; // Obstacle found (card)
        }
        // Also avoid cells already used by other relations (lineMap)
        if (lineMap[cellKey] && lineMap[cellKey] > 0) {
          return false; // Cell already has a relation passing through
        }
      }
      return true;
    }

    // Try direct path first if line of sight exists
    if (hasLineOfSight(start, end)) {
      const directPath = [];
      const dx = end.cellX - start.cellX;
      const dy = end.cellY - start.cellY;
      const steps = Math.max(Math.abs(dx), Math.abs(dy));

      for (let i = 0; i <= steps; i++) {
        const x = start.cellX + Math.round((dx * i) / steps);
        const y = start.cellY + Math.round((dy * i) / steps);
        directPath.push({ cellX: x, cellY: y });
      }
      return directPath;
    }

    // Calculate direction change penalty to encourage smoother paths
    function getDirectionChangePenalty(currentKey, newKey) {
      if (!cameFrom[currentKey]) return 0;

      const [cx, cy] = currentKey.split(',').map(Number);
      const [px, py] = cameFrom[currentKey].split(',').map(Number);
      const [nx, ny] = newKey.split(',').map(Number);

      // Calculate previous and current directions
      const prevDx = cx - px;
      const prevDy = cy - py;
      const currDx = nx - cx;
      const currDy = ny - cy;

      // If direction changed, add penalty
      if (prevDx !== currDx || prevDy !== currDy) {
        return 1; // Small penalty for direction changes
      }
      return 0;
    }

    while (openSet.size > 0) {
      // Find node in openSet with lowest fScore
      let currentKey = null;
      let minF = Infinity;
      for (const k of openSet) {
        if (fScore[k] < minF) {
          minF = fScore[k];
          currentKey = k;
        }
      }
      if (currentKey === null) {
        // No reachable node found
        break;
      }
      const [cx, cy] = currentKey.split(',').map(Number);
      if (cx === end.cellX && cy === end.cellY) {
        // Reconstruct path
        const path = [{ cellX: cx, cellY: cy }];
        let k = currentKey;
        while (cameFrom[k]) {
          k = cameFrom[k];
          const [px, py] = k.split(',').map(Number);
          path.push({ cellX: px, cellY: py });
        }
        return path.reverse();
      }
      openSet.delete(currentKey);
      for (const [dx, dy] of directions) {
        const nx = cx + dx;
        const ny = cy + dy;
        const nKey = key(nx, ny);

        // Base cost calculation
        const occupied = !!gridMap[nKey];
        const linePenalty = lineMap[nKey] ? 2 : 0;
        const directionChangePenalty = getDirectionChangePenalty(currentKey, nKey);
        const baseCost = occupied ? 10 : 1;

        const tentativeG = gScore[currentKey] + baseCost + linePenalty + directionChangePenalty;

        if (tentativeG < (gScore[nKey] ?? Infinity)) {
          cameFrom[nKey] = currentKey;
          gScore[nKey] = tentativeG;
          fScore[nKey] = tentativeG + improvedHeuristic({ cellX: nx, cellY: ny }, end);
          openSet.add(nKey);
        }
      }
    }
    // No path found
    return null;
  }

  // Card selection logic for relation endpoints
  const handleSelectCardForRelation = (card) => {
    if (!isCreatingRelation) return;
    if (relationStart === 'pending') {
      setRelationStart({
        canvas_card_id: card.canvas_card_id,
        card_id: card.card_id,
        cellX: card.pos_x,
        cellY: card.pos_y
      });
    } else if (relationEnd === 'pending') {
      setRelationEnd({
        canvas_card_id: card.canvas_card_id,
        card_id: card.card_id,
        cellX: card.pos_x,
        cellY: card.pos_y
      });
    }
  };

  // Compute path when both endpoints are set
  useEffect(() => {
    if (
      isCreatingRelation &&
      relationStart && relationStart !== 'pending' &&
      relationEnd && relationEnd !== 'pending'
    ) {
      const gridMap = getGridMap(cards);
      // Build lineMap from all existing relations' paths
      const lineMap = {};
      relations.forEach(rel => {
        if (rel.path) {
          rel.path.forEach(({ cellX, cellY }) => {
            const key = `${cellX},${cellY}`;
            lineMap[key] = (lineMap[key] || 0) + 1;
          });
        }
      });
      const path = findPathBetweenCells(
        { cellX: relationStart.cellX, cellY: relationStart.cellY },
        { cellX: relationEnd.cellX, cellY: relationEnd.cellY },
        gridMap,
        lineMap
      );
      setRelationPath(path);
    } else {
      setRelationPath(null);
    }
  }, [isCreatingRelation, relationStart, relationEnd, cards, relations]);

  // Centralized function to fetch relations and compute paths
  const fetchAndProcessRelations = async (canvasId, cards, setRelations, setRelationTypes, getGridMap, findPathBetweenCells, cardsArrayOverride) => {
    const relationsRes = await api.get(`/api/canvas/${canvasId}/relations`);
    const relsRaw = relationsRes.data || [];
    const gridMap = getGridMap(cardsArrayOverride || cards);
    const lineMap = {};
    // Collect all unique types from the loaded relations
    const loadedTypes = relsRaw
      .map(rel => rel.relation_type)
      .filter(type => type && typeof type === 'string');
    setRelationTypes(prev => {
      const set = new Set([...prev, ...loadedTypes]);
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    });
    const updatedRelations = relsRaw.map((rel, idx) => {
      const startExists = gridMap[`${rel.start_pos_x},${rel.start_pos_y}`] !== undefined;
      const endExists = gridMap[`${rel.end_pos_x},${rel.end_pos_y}`] !== undefined;

      const path = findPathBetweenCells(
        { cellX: rel.start_pos_x, cellY: rel.start_pos_y },
        { cellX: rel.end_pos_x, cellY: rel.end_pos_y },
        gridMap,
        lineMap
      );
      // Add this path to the lineMap for subsequent relations
      if (Array.isArray(path)) {
        path.forEach(({ cellX, cellY }) => {
          const key = `${cellX},${cellY}`;
          lineMap[key] = (lineMap[key] || 0) + 1;
        });
      }
      return {
        id: rel.canvas_relation_id,
        type: rel.relation_type ?? null,
        color: rel.relation_color,
        start: { cardId: rel.start_card_id, canvasCardId: rel.start_canvas_card_id, cellX: rel.start_pos_x, cellY: rel.start_pos_y },
        end: { cardId: rel.end_card_id, canvasCardId: rel.end_canvas_card_id, cellX: rel.end_pos_x, cellY: rel.end_pos_y },
        path: Array.isArray(path) ? path : [],
      };
    });
    setRelations(updatedRelations);
    const allPathsValid = updatedRelations.every(rel => Array.isArray(rel.path));
    if (!allPathsValid) {
    }
  };

  // When a new relation is created, POST to backend and use returned relation
  useEffect(() => {
    if (
      isCreatingRelation &&
      relationStart && relationStart !== 'pending' &&
      relationEnd && relationEnd !== 'pending' &&
      relationColor &&
      relationPath &&
      relationPath.length > 1
    ) {
      // POST to backend
      api.post(`/api/canvas/${canvas.canvas_id}/relations`, {
        start_canvas_card_id: relationStart.canvas_card_id,
        end_canvas_card_id: relationEnd.canvas_card_id,
        relation_type: relationType,
        relation_color: relationColor
      }).then(res => {
        const rel = res.data;
        setRelations(prev => [
          ...prev,
          {
            id: rel.canvas_relation_id,
            type: rel.relation_type ?? null,
            color: rel.relation_color,
            start: { cardId: rel.start_card_id, canvasCardId: rel.start_canvas_card_id, cellX: relationStart.cellX, cellY: relationStart.cellY },
            end: { cardId: rel.end_card_id, canvasCardId: rel.end_canvas_card_id, cellX: relationEnd.cellX, cellY: relationEnd.cellY },
            path: relationPath,
          }
        ]);
        setIsCreatingRelation(false);
        setRelationStart(null);
        setRelationEnd(null);
        setRelationType('');
        setRelationColor('#ff0000');
        setRelationPath(null);
        setSelectedRelation(rel.canvas_relation_id);
      });
    }
    // eslint-disable-next-line
  }, [relationPath]);

  // Delete relation handler (call backend)
  const handleDeleteRelation = (id) => {
    if (!canvas || !canvas.canvas_id) return;
    api.delete(`/api/canvas/${canvas.canvas_id}/relations/${id}`).then(() => {
      setRelations(prev => prev.filter(r => r.id !== id));
      setSelectedRelation(null);
    });
  };

  // Update relation handler (call backend)
  const handleUpdateRelation = (id, updates) => {
    if (!canvas || !canvas.canvas_id) return;
    api.patch(`/api/canvas/${canvas.canvas_id}/relations/${id}`, updates).then(res => {
      const updated = res.data;
      setRelations(prev => prev.map(r => r.id === id ? {
        ...r,
        type: updated.relation_type ?? null,
        color: updated.relation_color,
        // Update start/end fields if they were changed
        ...(updates.start_canvas_card_id && {
          start: {
            ...r.start,
            canvasCardId: updates.start_canvas_card_id
          }
        }),
        ...(updates.end_canvas_card_id && {
          end: {
            ...r.end,
            canvasCardId: updates.end_canvas_card_id
          }
        })
      } : r));
    });
  };

  // Helper: render text with symbol references (copied from GridCanvasRenderer)
  function renderTextWithSymbols(text, symbols, sizePx = 18) {
    if (!text) return null;
    const symbolRegex = /\{\{symbol:(\d+)\}\}/gi;
    const parts = [];
    let lastIdx = 0;
    text.replace(symbolRegex, (match, id, offset) => {
      if (offset > lastIdx) parts.push(text.slice(lastIdx, offset));
      const symbolId = parseInt(id, 10);
      const symbol = symbols.find(s => s.symbol_id === symbolId);
      if (symbol && symbol.symbol) {
        parts.push(
          <img
            key={offset}
            src={symbol.symbol}
            alt={`Symbol ${symbolId}`}
            style={{ width: sizePx, height: sizePx, objectFit: 'contain', verticalAlign: 'middle', display: 'inline-block' }}
          />
        );
      } else {
        parts.push(match);
      }
      lastIdx = offset + match.length;
      return match;
    });
    if (lastIdx < text.length) parts.push(text.slice(lastIdx));
    return parts;
  }

  // Click-away handler: cleans up relation creation and property panels
  useEffect(() => {
    function handleClick(event) {
      // If click is inside the right panel, do nothing
      if (rightPanelRef.current && rightPanelRef.current.contains(event.target)) return;
      // If waiting for the user to select a card for start or end, do not clean up
      if (relationStart === 'pending' || relationEnd === 'pending') return;
      // If any property panel or relation creation is open, clean up
      let didCleanup = false;
      if (isCreatingRelation) {
        setIsCreatingRelation(false);
        setRelationStart(null);
        setRelationEnd(null);
        setRelationType("");
        setRelationColor("#ff0000");
        setRelationPath(null);
        didCleanup = true;
      }
      if (selectedRelation) {
        setSelectedRelation(null);
        didCleanup = true;
      }
      // Optionally, blur focus if any cleanup happened
      if (didCleanup && document.activeElement) {
        document.activeElement.blur();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isCreatingRelation, selectedRelation, relationStart, relationEnd]);

  // Handler for user text change on special cards
  const handleUserTextChange = async (card, newText) => {
    setCards(prevCards => prevCards.map(c =>
      c.canvas_card_id === card.canvas_card_id ? { ...c, user_text: newText } : c
    ));
    setSelectedTextOption(opt => ({ ...opt, [card.canvas_card_id]: card.selected_option_index || 1 }));
    try {
      await api.put(`/api/canvas/${canvas.canvas_id}/cards/${card.canvas_card_id}`, {
        user_text: newText,
        selected_option_index: card.selected_option_index || 1
      });
    } catch (err) {
      setError('Failed to save text.');
    }
  };

  // Ensure unlimited availability for deck 0, card type 0
  useEffect(() => {
    setUnavailableCardTypes(prev => {
      const newMap = new Map(prev);
      return newMap;
    });
  }, [unavailableCardTypes]);

  if (loading) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        fontSize: 18,
        color: "#666"
      }}>
        Loading canvas...
      </div>
    );
  }

  if (error && !canvas) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        gap: 16
      }}>
        <div style={{ color: "red", fontSize: 18 }}>{error}</div>
        <button
          onClick={() => navigate("/dashboard")}
          style={{
            padding: "8px 16px",
            backgroundColor: "#4a90e2",
            color: "white",
            border: "none",
            borderRadius: 4,
            cursor: "pointer"
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const selectedCard = cards.find(c => c.canvas_card_id === selectedCanvasCardIds[0]);
  const selectedCards = selectedCanvasCardIds
    .map(id => cards.find(c => c.canvas_card_id === id))
    .filter(Boolean);

  return (
    <div style={{
      height: "100vh",
      width: "100vw",
      background: canvas?.background_image && canvas.background_image !== 'default'
        ? `url("/images/${canvas.background_image}") repeat center center / contain`
        : 'radial-gradient(circle at center, #1e293b 0%, #0f172a 100%)',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Full-screen overlay for PNG export */}
      {isCreatingImage && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, fontSize: '16px' }}>Creating image...</p>
          </div>
        </div>
      )}
      <div style={{
        position: 'fixed',
        top: 12,
        left: 'calc(var(--left-panel-width, 250px) + 20px + 54px)',
        zIndex: 2000
      }}>
        <ToursMenu isCanvas={true} />
      </div>
      <ResizablePanels
        initialLeftWidth={300}
        initialRightWidth={420}
        left={
          <div id="tour-canvas-panel-left" ref={leftPanelRef} style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            margin: 0,
            padding: 8,
            background: 'rgba(15, 23, 42, 0.4)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            boxSizing: 'border-box'
          }}>
            <div style={{ marginBottom: 16, flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
              {/* Show the Symbols of the Card types that are available for the canvas grouped by selected Decks. Only show the Symbol and use a tooltip what card type it is in the language specified for the canvas. */}
              {(() => {
                // Group selected decks into main and extension decks
                let selectedDeckIds = canvas?.selected_decks?.map(id => parseInt(id)) || [];
                // Ensure deck 0 is always present and at the top
                if (!selectedDeckIds.includes(0)) {
                  selectedDeckIds.unshift(0);
                } else {
                  selectedDeckIds = [0, ...selectedDeckIds.filter(id => id !== 0)];
                }
                const selectedDecks = decks.filter(deck => selectedDeckIds.includes(deck.deck_id));
                // Helper to get display name for sorting
                const getDeckName = deck =>
                  deck.translations?.[canvas.language]?.name ||
                  deck.translations?.en?.name ||
                  deck.name ||
                  `Deck ${deck.deck_id}`;
                // --- Special rendering for deck 0 at the top ---
                const deckZero = selectedDecks.find(deck => deck.deck_id === 0);
                let restDecks = selectedDecks.filter(deck => deck.deck_id !== 0);
                // Render deck 0 if present
                let deckZeroSection = null;
                if (deckZero) {
                  // Always show card type 0 for deck 0
                  let deckZeroCardTypes = deckZero.card_types && deckZero.card_types.length > 0
                    ? deckZero.card_types
                    : [0];
                  deckZeroSection = (
                    <div id="tour-deck-0" key={0} style={{ marginBottom: 12 }}>
                      <h3 style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '1.8rem',
                        fontWeight: 700,
                        marginBottom: 16,
                        color: "#f8fafc",
                        letterSpacing: "-0.02em",
                        fontFamily: "'Outfit', sans-serif"
                      }}>
                        <img src="/images/StoryWeaverLogo.svg" alt="StoryWeaver Logo" style={{ height: '1.8rem', width: 'auto' }} />
                        {getDeckName(deckZero)}
                      </h3>
                      {/* Container for card types */}
                      <div id="tour-draw-cards" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))',
                        gap: 12,
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        {/* Card Type 0 - Custom Text Card */}
                        {deckZeroCardTypes.map(cardTypeId => {
                          const cardType = cardTypes.find(ct => Number(ct.type_id) === Number(cardTypeId));
                          if (!cardType && cardTypeId === 0) {
                            const fallback = cardTypes.find(ct => Number(ct.type_id) === 0);
                            if (!fallback) return null;
                            return (
                              <div
                                id="tour-deck-0-card-0"
                                key={0}
                                title={`${fallback.name}${fallback.description ? ` - ${fallback.description}` : ''}`}
                                style={{
                                  width: 40,
                                  height: 40,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  opacity: 1
                                }}
                                onClick={() => handleDrawCard(0, 0)}
                              >
                                {<img
                                  src={fallback.symbol_filename}
                                  alt={fallback.name}
                                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />}
                              </div>
                            );
                          }
                          if (!cardType) return null;
                          const isUnavailable = unavailableCardTypes.has(`0:${cardType.type_id}`);
                          return (
                            <div
                              id={`tour-deck-0-card-${cardType.type_id}`}
                              key={cardType.type_id}
                              title={`${cardType.name}${cardType.description ? ` - ${cardType.description}` : ''}${isUnavailable ? ' (No cards available)' : ''}`}
                              style={{
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: isUnavailable ? "not-allowed" : "pointer",
                                transition: "all 0.2s",
                                opacity: isUnavailable ? 0.3 : 1
                              }}
                              onClick={() => !isUnavailable && handleDrawCard(0, cardType.type_id)}
                            >
                              {<img
                                src={cardType.symbol_filename}
                                alt={cardType.name}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              />}
                            </div>
                          );
                        })}

                        {/* Special Tools Section */}
                        <div
                          id="tour-relation-btn"
                          style={{
                            width: 40,
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            opacity: 1,
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            borderRadius: 8,
                            background: "rgba(255, 255, 255, 0.05)"
                          }}
                          title="Add Relation"
                          onClick={() => {
                            setIsCreatingRelation(true);
                            setSelectedCanvasCardIds([]);
                            setRelationStart(null);
                            setRelationEnd(null);
                            setRelationType("");
                            setRelationColor("#ff0000");
                            setRelationPath(null);
                            setSelectedRelation(null);
                          }}
                        >
                          <img
                            src="/images/Relation.svg"
                            alt="Add Relation"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                          />
                        </div>

                        {/* Canvas Tool */}
                        <div
                          id="tour-canvas-ref-btn"
                          style={{
                            width: 40,
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            opacity: 1,
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            borderRadius: 8,
                            background: "rgba(255, 255, 255, 0.05)"
                          }}
                          title="Add Canvas Reference"
                          onClick={async () => {
                            if (!canvas) return;
                            setError("");
                            try {
                              let url = `/api/canvas/${canvas.canvas_id}/draw/0`;
                              url += `?deckId=0`;
                              const res = await api.get(url);
                              const card = res.data;
                              if (!card || card.card_id == null) {
                                setError("No available card of this type in this deck.");
                                return;
                              }
                              const { cellX, cellY } = findNearestEmptyCell();
                              const addRes = await api.post(`/api/canvas/${canvas.canvas_id}/cards`, {
                                cardId: card.card_id,
                                posX: cellX,
                                posY: cellY,
                                groupId: null,
                                selected_option_index: 1,
                                user_text: '{Canvas:}'
                              });
                              const canvasCardId = addRes.data && addRes.data.canvasCardId;
                              const updated = await api.get(`/api/canvas/${canvas.canvas_id}`);
                              setCards(updated.data.cards || []);
                              if (Array.isArray(updated.data.cards)) {
                                setSelectedTextOption(prev => {
                                  const newOptions = { ...prev };
                                  updated.data.cards.forEach(card => {
                                    if (card.selected_option_index != null) {
                                      newOptions[card.canvas_card_id] = card.selected_option_index;
                                    }
                                  });
                                  return newOptions;
                                });
                              }
                              await fetchAndProcessRelations(canvas.canvas_id, updated.data.cards || [], setRelations, setRelationTypes, getGridMap, findPathBetweenCells, updated.data.cards || []);
                              if (canvasCardId) {
                                setSelectedCanvasCardIds([canvasCardId]);
                                setSelectedTextOption(opt => ({ ...opt, [canvasCardId]: 1 }));
                              }
                              setError("");
                            } catch (err) {
                              console.error('Failed to create canvas reference card:', err);
                              setError(err?.response?.data?.error || err.message || "Failed to create canvas reference card");
                            }
                          }}
                        >
                          <img
                            src="/images/Canvas.svg"
                            alt="Canvas Tools"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                }
                // --- End special rendering for deck 0 ---
                // Continue with the rest of the rendering as before, using restDecks instead of selectedDecks
                const mainDecks = restDecks.filter(deck => !deck.is_extension);
                const extensionDecks = restDecks.filter(deck => deck.is_extension);
                // Sort main decks alphabetically
                const sortedMainDecks = [...mainDecks].sort((a, b) => getDeckName(a).localeCompare(getDeckName(b)));
                // Group extension decks by main_deck_id and sort them
                const extensionsByMain = {};
                for (const ext of extensionDecks) {
                  if (!extensionsByMain[ext.main_deck_id]) extensionsByMain[ext.main_deck_id] = [];
                  extensionsByMain[ext.main_deck_id].push(ext);
                }
                for (const key in extensionsByMain) {
                  extensionsByMain[key].sort((a, b) => getDeckName(a).localeCompare(getDeckName(b)));
                }

                // Helper: get card types for a deck
                const getCardTypes = (deck) =>
                  deck.card_types
                    ? cardTypes.filter(ct => deck.card_types.includes(Number(ct.type_id)))
                    : [];

                if (canvas.shuffle_extensions) {
                  // Shuffle: merge extension decks into main deck
                  return (
                    <>
                      {deckZeroSection}
                      {sortedMainDecks.map(mainDeck => {
                        // Find extension decks belonging to this main deck
                        const extensions = extensionsByMain[mainDeck.deck_id] || [];
                        // Gather all card type IDs from main deck and its extensions
                        const allTypeIds = new Set([
                          ...(mainDeck.card_types || []),
                          ...extensions.flatMap(ext => ext.card_types || [])
                        ]);
                        // Get the card type objects for these IDs
                        const allCardTypes = cardTypes.filter(ct => allTypeIds.has(Number(ct.type_id)));
                        // Gather all deck IDs for this main deck and its extensions
                        const allDeckIds = [mainDeck.deck_id, ...extensions.map(ext => ext.deck_id)];
                        return (
                          <div id={`tour-deck-${mainDeck.deck_id}`} key={mainDeck.deck_id} style={{ marginBottom: 12 }}>
                            <h3 style={{
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: 16,
                              fontWeight: 700,
                              marginBottom: 8,
                              color: "#f8fafc",
                              letterSpacing: "-0.02em",
                              fontFamily: "'Outfit', sans-serif"
                            }}>
                              {mainDeck.deck_symbol_path ? (
                                <span style={{
                                  width: 32,
                                  height: 32,
                                  marginRight: 12,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}>
                                  <img
                                    src={mainDeck.deck_symbol_path}
                                    alt="Deck Symbol"
                                    style={{ objectFit: 'contain', width: '32px', height: '32px' }}
                                  />
                                </span>
                              ) : null}
                              {
                                mainDeck.translations?.[canvas.language]?.name ||
                                mainDeck.translations?.en?.name ||
                                mainDeck.name ||
                                `Deck ${mainDeck.deck_id}`
                              }
                            </h3>
                            <div style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))",
                              gap: 4,
                              background: "rgba(15, 23, 42, 0.5)",
                              padding: 8,
                              borderRadius: 12,
                              border: "1px solid rgba(255, 255, 255, 0.05)"
                            }}>
                              {allCardTypes.map(cardType => {
                                const deckKey = canvas.shuffle_extensions ? allDeckIds.sort().join(',') : mainDeck.deck_id.toString();
                                // Calculate total remaining count across all relevant deck IDs
                                const count = allDeckIds.reduce((sum, dId) => sum + (cardAvailability[`${dId}:${cardType.type_id}`] || 0), 0);
                                const isUnavailable = unavailableCardTypes.has(`${deckKey}:${cardType.type_id}`) || (!canvas?.allow_duplicate_draws && count === 0);
                                const availabilityLabel = canvas?.allow_duplicate_draws ? '' : (isUnavailable ? ' (No cards available)' : ` (${count} remaining)`);
                                return (
                                  <div
                                    id={`tour-deck-${mainDeck.deck_id}-card-${cardType.type_id}`}
                                    key={cardType.type_id}
                                    title={`${cardType.name}${cardType.description ? ` - ${cardType.description}` : ''}${availabilityLabel}`}
                                    style={{
                                      width: 40,
                                      height: 40,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: isUnavailable ? "not-allowed" : "pointer",
                                      transition: "all 0.2s",
                                      opacity: isUnavailable ? 0.3 : 1
                                    }}
                                    onClick={() => !isUnavailable && handleDrawCard(canvas.shuffle_extensions ? allDeckIds : mainDeck.deck_id, cardType.type_id)}
                                  >
                                    {<img
                                      src={cardType.symbol_filename}
                                      alt={cardType.name}
                                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                    }
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  );
                } else {
                  // No shuffle: show main and extension decks separately, group extensions under their main deck
                  return (
                    <>
                      {deckZeroSection}
                      {sortedMainDecks.map(mainDeck => {
                        // Main deck card types
                        const mainCardTypes = getCardTypes(mainDeck);
                        // Find extension decks belonging to this main deck
                        const extensions = extensionsByMain[mainDeck.deck_id] || [];
                        return (
                          <div id={`tour-deck-${mainDeck.deck_id}`} key={mainDeck.deck_id} style={{ marginBottom: 12 }}>
                            <h3 style={{
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: 16,
                              fontWeight: 700,
                              marginBottom: 8,
                              color: "#f8fafc",
                              letterSpacing: "-0.02em",
                              fontFamily: "'Outfit', sans-serif"
                            }}>
                              {mainDeck.deck_symbol_path ? (
                                <span style={{
                                  width: 32,
                                  height: 32,
                                  marginRight: 12,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}>
                                  <img
                                    src={mainDeck.deck_symbol_path}
                                    alt="Deck Symbol"
                                    style={{ objectFit: 'contain', width: '32px', height: '32px' }}
                                  />
                                </span>
                              ) : null}
                              {
                                mainDeck.translations?.[canvas.language]?.name ||
                                mainDeck.translations?.en?.name ||
                                mainDeck.name ||
                                `Deck ${mainDeck.deck_id}`
                              }
                            </h3>
                            <div style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))",
                              gap: 4,
                              background: "rgba(15, 23, 42, 0.5)",
                              padding: 8,
                              borderRadius: 12,
                              border: "1px solid rgba(255, 255, 255, 0.05)"
                            }}>
                              {mainCardTypes.map(cardType => {
                                const count = cardAvailability[`${mainDeck.deck_id}:${cardType.type_id}`] || 0;
                                const isUnavailable = unavailableCardTypes.has(`${mainDeck.deck_id}:${cardType.type_id}`) || (!canvas?.allow_duplicate_draws && count === 0);
                                const availabilityLabel = canvas?.allow_duplicate_draws ? '' : (isUnavailable ? ' (No cards available)' : ` (${count} remaining)`);
                                return (
                                  <div
                                    id={`tour-deck-${mainDeck.deck_id}-card-${cardType.type_id}`}
                                    key={cardType.type_id}
                                    title={`${cardType.name}${cardType.description ? ` - ${cardType.description}` : ''}${availabilityLabel}`}
                                    style={{
                                      width: 40,
                                      height: 40,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: isUnavailable ? "not-allowed" : "pointer",
                                      transition: "all 0.2s",
                                      opacity: isUnavailable ? 0.3 : 1
                                    }}
                                    onClick={() => !isUnavailable && handleDrawCard(mainDeck.deck_id, cardType.type_id)}
                                  >
                                    {<img
                                      src={cardType.symbol_filename}
                                      alt={cardType.name}
                                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                    }
                                  </div>
                                );
                              })}
                            </div>
                            {/* Extension decks under this main deck */}
                            {extensions.map(extDeck => (
                              <div id={`tour-deck-${extDeck.deck_id}`} key={extDeck.deck_id} style={{ margin: '0 0 0 8px' }}>
                                <h3 style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  fontSize: 14,
                                  fontWeight: 700,
                                  marginBottom: 8,
                                  color: "#f8fafc",
                                  fontFamily: "'Outfit', sans-serif"
                                }}>
                                  {extDeck.deck_symbol_path ? (
                                    <span style={{
                                      width: 24,
                                      height: 24,
                                      marginRight: 10,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center"
                                    }}>
                                      <img
                                        src={extDeck.deck_symbol_path}
                                        alt="Deck Symbol"
                                        style={{ objectFit: 'contain', width: '24px', height: '24px' }}
                                      />
                                    </span>
                                  ) : null}
                                  {
                                    extDeck.translations?.[canvas.language]?.name ||
                                    extDeck.translations?.en?.name ||
                                    extDeck.name ||
                                    `Extension Deck ${extDeck.deck_id}`
                                  }
                                </h3>
                                <div style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))",
                                  gap: 4,
                                  background: "rgba(15, 23, 42, 0.5)",
                                  padding: 8,
                                  borderRadius: 12,
                                  border: "1px solid rgba(255, 255, 255, 0.05)"
                                }}>
                                  {getCardTypes(extDeck).map(cardType => {
                                    const count = cardAvailability[`${extDeck.deck_id}:${cardType.type_id}`] || 0;
                                    const isUnavailable = unavailableCardTypes.has(`${extDeck.deck_id}:${cardType.type_id}`) || (!canvas?.allow_duplicate_draws && count === 0);
                                    const availabilityLabel = canvas?.allow_duplicate_draws ? '' : (isUnavailable ? ' (No cards available)' : ` (${count} remaining)`);
                                    return (
                                      <div
                                        id={`tour-deck-${extDeck.deck_id}-card-${cardType.type_id}`}
                                        key={cardType.type_id}
                                        title={`${cardType.name}${cardType.description ? ` - ${cardType.description}` : ''}${availabilityLabel}`}
                                        style={{
                                          width: 40,
                                          height: 40,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          cursor: isUnavailable ? "not-allowed" : "pointer",
                                          transition: "all 0.2s",
                                          opacity: isUnavailable ? 0.3 : 1
                                        }}
                                        onClick={() => !isUnavailable && handleDrawCard(extDeck.deck_id, cardType.type_id)}
                                      >
                                        {<img
                                          src={cardType.symbol_filename}
                                          alt={cardType.name}
                                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                        />
                                        }
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </>
                  );
                }
              })()}
            </div>
            <div style={{ display: 'flex', gap: '8px', padding: '0 16px', marginBottom: '16px' }}>
              <button
                id="tour-export-png"
                style={{
                  flex: 1,
                  boxSizing: 'border-box',
                  height: '100px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  margin: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  padding: 0
                }}
                className="glass-action-btn"
                onClick={() => {
                  setIsCreatingImage(true);
                  exportButtonRef.current?.handleExportPNG()
                    .catch(() => {})
                    .finally(() => setIsCreatingImage(false));
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.4)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <img
                  src="/images/Export_PNG.png"
                  alt="Export as image"
                  style={{
                    height: '94px',
                    width: 'auto',
                    objectFit: 'contain'
                  }}
                />
              </button>
              <CanvasExportButton
                ref={exportButtonRef}
                cards={cards}
                relations={relations}
                canvas={canvas}
                symbols={symbols}
                selectedTextOption={selectedTextOption}
                language={canvas?.language || 'en'}
                decks={decks}
                cardTypes={cardTypes}
              />
              <button
                id="tour-export-ai-story"
                style={{
                  flex: 1,
                  boxSizing: 'border-box',
                  height: '100px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  margin: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  padding: 0
                }}
                className="glass-action-btn"
                onClick={() => setShowStoryExport(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.4)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <img
                  src="/images/Export_IA.png"
                  alt="Export for AI"
                  style={{
                    height: '94px',
                    width: 'auto',
                    objectFit: 'contain'
                  }}
                />
              </button>
            </div>
          </div>
        }
        center={
          <div id="tour-canvas-grid" ref={centerPanelRef} style={{
            height: "100%",
            width: "100%",
            position: "relative",
            background: "transparent",
            padding: 0,
            border: "none",
            outline: "none"
          }}>
            <GridCanvasRenderer
              cards={cards.map(card => ({ ...card, cell_x: card.pos_x, cell_y: card.pos_y }))}
              selectedCardIds={selectedCanvasCardIds}
              selectedTextOption={selectedTextOption}
              onSelectCard={handleSelectCard}
              onSelectTextOption={(cardId, optionIdx) => setSelectedTextOption(opt => ({ ...opt, [cardId]: optionIdx }))}
              gridOffset={gridOffset}
              zoom={zoom}
              onZoomChange={setZoom}
              onMoveCard={handleMoveCard}
              onMoveSelectedCards={handleMoveSelectedCards}
              onGridOffsetChange={setGridOffset}
              language={canvas?.language || 'en'}
              onContainerSizeChange={setActualCanvasSize}
              symbols={symbols}
              decks={decks}
              cardTypes={cardTypes}
              relationPreviewPath={relationPath}
              relationPreviewColor={relationColor}
              relations={relations}
              onRelationClick={relationId => {
                setSelectedRelation(relationId);
                setSelectedCanvasCardIds([]);
              }}
              selectedRelation={selectedRelation}
              // Box selection props
              isBoxSelecting={isBoxSelecting}
              boxSelectionStart={boxSelectionStart}
              boxSelectionEnd={boxSelectionEnd}
              onBoxSelectionStart={handleBoxSelectionStart}
              onBoxSelectionUpdate={handleBoxSelectionUpdate}
              onBoxSelectionEnd={handleBoxSelectionEnd}
              onUserTextChange={handleUserTextChange}
              userCanvases={userCanvases}
              showImages={canvas?.showImages}
              backgroundImage={canvas?.background_image}
            />
          </div >
        }


        right={
          < div ref={rightPanelRef} style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            margin: 0,
            padding: 8,
            background: 'rgba(15, 23, 42, 0.4)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            boxSizing: 'border-box'
          }}>
            {/* MiniMap */}
            < MiniMap
              cards={cards}
              gridOffset={gridOffset}
              zoom={zoom}
              centerPanelSize={actualCanvasSize}
              showImages={canvas?.showImages}
              backgroundImage={canvas?.background_image}
              onJumpTo={(cellX, cellY) => {
                // Center the viewport on the clicked cell using correct cell size
                const CELL_SIZE = 122; // Match the constant in GridCanvasRenderer
                setGridOffset({
                  x: -cellX * CELL_SIZE * zoom + actualCanvasSize.width / 2,
                  y: -cellY * CELL_SIZE * zoom + actualCanvasSize.height / 2
                });
              }}
            />

            {/* Properties panels */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
              {selectedRelation ? (
                <div style={{ padding: 16, flex: 1, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <h3>Relation Properties</h3>
                  {(() => {
                    const rel = relations.find(r => r.id === selectedRelation);
                    if (!rel) return null;
                    return (
                      <>
                        <div style={{ marginBottom: 12 }}>
                          <label>Type: </label>
                          <select
                            value={rel.type ?? ''}
                            onChange={e => {
                              if (e.target.value === '__add_new__') {
                                setAddingRelationType(true);
                                setNewRelationType('');
                              } else {
                                handleUpdateRelation(rel.id, { relation_type: e.target.value || null });
                              }
                            }}
                          >
                            <option value="">(none)</option>
                            {[...relationTypes].sort((a, b) => a.localeCompare(b)).map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                            <option value="__add_new__">+ Add new type...</option>
                          </select>
                        </div>
                        {addingRelationType && (
                          <div style={{ marginTop: 8 }}>
                            <input
                              type="text"
                              value={newRelationType}
                              onChange={e => setNewRelationType(e.target.value)}
                              placeholder="Enter new relation type"
                              style={{ marginRight: 8 }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  const val = newRelationType.trim();
                                  if (val && !relationTypes.includes(val)) {
                                    setRelationTypes(prev => [...prev, val]);
                                    handleUpdateRelation(rel.id, { relation_type: val });
                                    setAddingRelationType(false);
                                    setNewRelationType('');
                                  }
                                }
                              }}
                            />
                            <button
                              onClick={() => {
                                const val = newRelationType.trim();
                                if (val && !relationTypes.includes(val)) {
                                  setRelationTypes(prev => [...prev, val]);
                                  handleUpdateRelation(rel.id, { relation_type: val });
                                  setAddingRelationType(false);
                                  setNewRelationType('');
                                }
                              }}
                              style={{ padding: '2px 8px' }}
                            >Add</button>
                            <button
                              onClick={() => {
                                setAddingRelationType(false);
                                setNewRelationType('');
                              }}
                              style={{ padding: '2px 8px', marginLeft: 4 }}
                            >Cancel</button>
                          </div>
                        )}
                        <div style={{ marginBottom: 12 }}>
                          <label>Color: </label>
                          <input
                            type="color"
                            value={rel.color}
                            onChange={e => handleUpdateRelation(rel.id, { relation_color: e.target.value })}
                            style={{ marginLeft: 8, verticalAlign: 'middle', width: 36, height: 36, border: 'none', borderRadius: 6, padding: 0, background: 'none' }}
                          />
                        </div>
                        <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
                          <button
                            style={{ background: relationEditPending === 'start' ? '#f39c12' : rel.start ? '#4a90e2' : '#222', color: 'white', border: 'none', borderRadius: 4, padding: '6px 12px', flex: 1 }}
                            onClick={() => {
                              setSelectedCanvasCardIds([]);
                              setRelationEditPending('start');
                              setRelationStart('pending');
                              setSelectedRelation(rel.id);
                            }}
                          >
                            {relationEditPending === 'start' ? 'Click a card to set Start' : rel.start && rel.start !== 'pending' ? (
                              <>Start: {(() => {
                                const card = cards.find(c => c.canvas_card_id === rel.start.canvasCardId || c.card_id === rel.start.cardId);
                                if (!card) return rel.start.cardId;
                                const lang = canvas?.language || 'en';
                                const texts = (card.texts || []).filter(t => t.language === lang);
                                const optIdx = selectedTextOption[card.canvas_card_id] || (texts[0]?.option_index ?? 1);
                                const textObj = texts.find(t => t.option_index === optIdx) || texts[0];
                                return textObj ? renderTextWithSymbols(textObj.text, symbols, 18) : card.card_id;
                              })()}</>
                            ) : 'Set Start Card'}
                          </button>
                          <button
                            style={{ background: relationEditPending === 'end' ? '#f39c12' : rel.end ? '#4a90e2' : '#222', color: 'white', border: 'none', borderRadius: 4, padding: '6px 12px', flex: 1 }}
                            onClick={() => {
                              setSelectedCanvasCardIds([]);
                              setRelationEditPending('end');
                              setRelationEnd('pending');
                              setSelectedRelation(rel.id);
                            }}
                          >
                            {relationEditPending === 'end' ? 'Click a card to set End' : rel.end && rel.end !== 'pending' ? (
                              <>End: {(() => {
                                const card = cards.find(c => c.canvas_card_id === rel.end.canvasCardId || c.card_id === rel.end.cardId);
                                if (!card) return rel.end.cardId;
                                const lang = canvas?.language || 'en';
                                const texts = (card.texts || []).filter(t => t.language === lang);
                                const optIdx = selectedTextOption[card.canvas_card_id] || (texts[0]?.option_index ?? 1);
                                const textObj = texts.find(t => t.option_index === optIdx) || texts[0];
                                return textObj ? renderTextWithSymbols(textObj.text, symbols, 18) : card.card_id;
                              })()}</>
                            ) : 'Set End Card'}
                          </button>
                        </div>
                        <div style={{ display: "flex", gap: 8, marginTop: 'auto', marginBottom: -10 }}>
                          <button
                            style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: 4, padding: '8px 16px', fontWeight: 'bold', cursor: 'pointer', width: '100%' }}
                            onClick={() => handleDeleteRelation(rel.id)}
                          >
                            Delete Relation
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : isCreatingRelation ? (
                <div id="tour-right-panel" style={{ padding: 16, flex: 1, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <h3>New Relation</h3>
                  <div style={{ marginBottom: 12 }}>
                    <label>Type: </label>
                    <select value={relationType} onChange={e => setRelationType(e.target.value)}>
                      <option value="">Select type</option>
                      {relationTypes.map(rt => (
                        <option key={rt} value={rt}>{rt}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label>Color: </label>
                    <input
                      type="color"
                      value={relationColor}
                      onChange={e => setRelationColor(e.target.value)}
                      style={{ marginLeft: 8, verticalAlign: 'middle', width: 36, height: 36, border: 'none', borderRadius: 6, padding: 0, background: 'none' }}
                    />
                  </div>
                  <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
                    <button
                      style={{ background: relationStart ? '#4a90e2' : '#222', color: 'white', border: 'none', borderRadius: 4, padding: '6px 12px', flex: 1 }}
                      onClick={() => {
                        setSelectedCanvasCardIds([]);
                        setRelationStart('pending');
                      }}
                    >
                      {relationStart && relationStart !== 'pending' ? (
                        <>Start: Card {(() => {
                          const card = cards.find(c => c.canvas_card_id === relationStart.canvas_card_id || c.card_id === relationStart.card_id);
                          if (!card) return relationStart.card_id;
                          const lang = canvas?.language || 'en';
                          const texts = (card.texts || []).filter(t => t.language === lang);
                          const optIdx = selectedTextOption[card.canvas_card_id] || (texts[0]?.option_index ?? 1);
                          const textObj = texts.find(t => t.option_index === optIdx) || texts[0];
                          return textObj ? renderTextWithSymbols(textObj.text, symbols, 18) : card.card_id;
                        })()}</>
                      ) : 'Set Start Card'}
                    </button>
                    <button
                      style={{ background: relationEnd ? '#4a90e2' : '#222', color: 'white', border: 'none', borderRadius: 4, padding: '6px 12px', flex: 1 }}
                      onClick={() => {
                        setSelectedCanvasCardIds([]);
                        setRelationEnd('pending');
                      }}
                    >
                      {relationEnd && relationEnd !== 'pending' ? (
                        <>End: Card {(() => {
                          const card = cards.find(c => c.canvas_card_id === relationEnd.canvas_card_id || c.card_id === relationEnd.card_id);
                          if (!card) return relationEnd.card_id;
                          const lang = canvas?.language || 'en';
                          const texts = (card.texts || []).filter(t => t.language === lang);
                          const optIdx = selectedTextOption[card.canvas_card_id] || (texts[0]?.option_index ?? 1);
                          const textObj = texts.find(t => t.option_index === optIdx) || texts[0];
                          return textObj ? renderTextWithSymbols(textObj.text, symbols, 18) : card.card_id;
                        })()}</>
                      ) : 'Set End Card'}
                    </button>
                  </div>
                </div>
              ) : selectedCards.length === 1 ? (
                selectedCard ? (
                  <CardPropertiesPanel
                    card={selectedCard}
                    selectedTextOption={selectedTextOption[selectedCard?.canvas_card_id]}
                    onSelectTextOption={async optionIdx => {
                      setSelectedTextOption(opt => ({ ...opt, [selectedCard?.canvas_card_id]: optionIdx }));
                      setCards(prevCards =>
                        prevCards.map(c =>
                          c.canvas_card_id === selectedCard?.canvas_card_id
                            ? { ...c, selected_option_index: optionIdx }
                            : c
                        )
                      );
                      try {
                        await api.put(`/api/canvas/${canvas.canvas_id}/cards/${selectedCard?.canvas_card_id}`, {
                          selected_option_index: optionIdx
                        });
                      } catch (err) {
                        setError('Failed to save selected text option.');
                      }
                    }}
                    language={canvas?.language || 'en'}
                    onDelete={() => handleDeleteMultipleCards([selectedCard?.canvas_card_id])}
                    symbols={symbols}
                    onUserTextChange={handleUserTextChange}
                    userCanvases={userCanvases}
                    currentCanvasId={canvas?.canvas_id}
                  />
                ) : null
              ) : selectedCards.length > 1 ? (
                selectedCards.length > 0 ? (
                  <div style={{ padding: 16, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <h3>Multiple Cards Selected ({selectedCards.length})</h3>
                    <div style={{ marginBottom: 12, flex: 1 }}>
                      <p>Multiple cards are selected. Drag to move them as a group.</p>
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 'auto', marginBottom: -10 }}>
                      <button
                        style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: 4, padding: '8px 16px', fontWeight: 'bold', cursor: 'pointer', width: '100%' }}
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to delete ${selectedCards.length} cards from the canvas?`)) {
                            handleDeleteMultipleCards(selectedCards.map(card => card.canvas_card_id));
                          }
                        }}
                      >
                        Delete All Selected Cards
                      </button>
                    </div>
                  </div>
                ) : null
              ) : (
                <CanvasPropertiesPanel
                  canvas={canvas}
                  decks={decks}
                  languages={languages}
                  onChange={handleCanvasChange}
                  onDelete={handleDelete}
                  saving={saving}
                  error={error}
                />
              )}
            </div>

          </div >
        }
      />

      {/* Story Export Panel */}
      {
        showStoryExport && (
          <StoryExportPanel
            canvasId={canvasId}
            canvasName={canvas?.name}
            cards={cards}
            relations={relations}
            language={canvas?.language || 'en'}
            cardTypes={cardTypes}
            decks={decks}
            onClose={() => setShowStoryExport(false)}
          />
        )
      }
    </div >
  );
};

export default Canvas; 